import Backbone from 'backbone';
import ko from 'knockout';
import _ from 'underscore';
import Promise from 'bluebird';
import pageContext from '@bingads-webui-static-legacy/page-context';
import tmpl from '@bingads-webui-campaign-legacy/crud-adgroup/template/mixed-mode-create.pug';
import AdGroupView from '@bingads-webui-campaign-legacy/crud-adgroup/view/create';
import AdGroupUpdateView from '@bingads-webui-campaign-legacy/crud-adgroup/view/update';
import jsdata from '@bingads-webui-component-legacy/js-data-campaign/index';
import * as i18nErrors from '@bingads-webui-component-legacy/api-errors-campaign/index';
import * as keywordAdGroupCreateDecorator from '@bingads-webui-campaign-legacy/crud-adgroup/view/create-keyword-adgroup-decorator';
import * as keywordAdGroupUpdateDecorator from '@bingads-webui-campaign-legacy/crud-adgroup/view/update-keyword-adgroup-decorator';
import * as autoTargetAdGroupCreateDecorator from '@bingads-webui-campaign-legacy/crud-adgroup/view/create-autotarget-adgroup-decorator';
import * as autoTargetAdGroupUpdateDecorator from '@bingads-webui-campaign-legacy/crud-adgroup/view/update-autotarget-adgroup-decorator';
import DSASettingsView from '@bingads-webui-campaign-legacy/dsa-settings/view/dsa-settings';
import ViewModel from '@bingads-webui-campaign-legacy/crud-adgroup/viewmodel/mixed-mode-create';
import * as scrollUtil from '@bingads-webui-component-legacy/extension/scrollTo';
import withUiBlock from '@bingads-webui-component-legacy/uiblocker/index';
import { adGroupTypeEnum } from '@bingads-webui-campaign-legacy/adgroup-create-advanced/util/adGroupTypeEnum';

const i18n = window.globalLegacyI18n.get('component/i18n/loader!campaignui/campaign-creation-advanced');

export default class MixedModeAdGroupCreate extends Backbone.View {
  initialize(options) {
    this.options = options;
    if (this.options && this.options.perfMarker) {
      this.showAdGroupKeywordMarker = this.options.perfMarker.createChild('Show_AdGroup_Keyword');
      this.showAdGroupKeywordMarker.willInit();
    }

    this.viewModel = new ViewModel();
    this.initializeAutoTargetAdGroupCreateView();
    this.initializeKeywordAdGroupCreateView();
    this.initializeDSASettings();
    this.modified = false;
    this.mounted = false;
  }

  get events() {
    return {
      'show.bs.collapse': 'onToggleSection',
      'hide.bs.collapse': 'onToggleSection',
    };
  }

  checkAdGroupNameDuplicates(adGroup) {
    const adGroupName = adGroup.ko_model.Name(); // eslint-disable-line
    const adGroupType = adGroup.ko_model.AdGroupType(); // eslint-disable-line
    const dynamicAdGroupSectionViewModel = this.autoTargetAdGroupCreateView.ko_model;
    const standardAdGroupSectionViewModel = this.keywordAdGroupCreateView.ko_model;
    const dynamicAdGroups = dynamicAdGroupSectionViewModel.adGroups();
    const standardAdGroups = standardAdGroupSectionViewModel.adGroups();

    let isDuplicate = false;

    _.chain(dynamicAdGroups.concat(standardAdGroups))
      .filter((adg) => adg !== adGroup)
      .each((adg) => {
        const name = adg.ko_model.Name().toLowerCase(); // eslint-disable-line

        if (_.isString(name) && name === adGroupName.toLowerCase() && adGroupName !== '') {
          isDuplicate = true;
        }
      });

    return isDuplicate;
  }

  checkAllAdGroupNameDuplicate() {
    const dynamicAdGroupSectionViewModel = this.autoTargetAdGroupCreateView.ko_model;
    const standardAdGroupSectionViewModel = this.keywordAdGroupCreateView.ko_model;
    const dynamicAdGroups = dynamicAdGroupSectionViewModel.adGroups();
    const standardAdGroups = standardAdGroupSectionViewModel.adGroups();
    const $dynamicAdGroupSection = this.$('#dynamicAdGroupSection');
    const $standardAdGroupSection = this.$('#standardAdGroupSection');

    _.each(dynamicAdGroups.concat(standardAdGroups), (adg) => {
      const isDuplicate = this.checkAdGroupNameDuplicates(adg);

      adg.ko_model.isNameDuplicate(isDuplicate);

      if (isDuplicate) {
        if (adg.ko_model.AdGroupType() === adGroupTypeEnum.SearchStandard) { // eslint-disable-line
          $standardAdGroupSection.collapse('show');
          this.removeStandardAdGroupSectionAriaExpandedAttribute();
        } else {
          $dynamicAdGroupSection.collapse('show');
        }
      }
    });
  }

  hasDSASettingsInCampaignSettings() {
    const campaign = this.options.context.get('Campaign');
    const hasDSASettings = campaign && campaign.DomainName != null;

    return hasDSASettings;
  }

  hasDisclaimerSettings() {
    const disclaimerSettings = this.options.context.get('Draft.Disclaimer');

    return !_.isEmpty(disclaimerSettings);
  }

  willShow() {
    this.autoTargetAdGroupCreateView.willShow(!this.hasDSASettingsInCampaignSettings(), this.mounted);
    this.keywordAdGroupCreateView.willShow(this.hasDSASettingsInCampaignSettings(), this.mounted);
    this.adjustSectionOrder();
    this.toggleDSASettings();
    this.mounted = true;

    if (this.showAdGroupKeywordMarker) {
      this.showAdGroupKeywordMarker.done();
    }
  }

  saveTargetingSource(id) {
    const retPromise = id && this.dsaSettingsView && this.dsaSettingsView.targetingSource ?
      this.dsaSettingsView.targetingSource.save(id) : Promise.resolve();

    return retPromise.then((result) => {
      if (_.result(pageContext.CurrentCampaign, 'Id') === id) {
        if (_.size(pageContext.CurrentCampaign.CampaignSettings) > 0 && this.targetingSource) {
          pageContext.CurrentCampaign.CampaignSettings[0].Source = this.targetingSource.koModel.source();
        }
      }
      return result;
    });
  }

  scrollToDSAError() {
    scrollUtil.scrollAndCenter(this.$('.dsa-settings-container .error'));
  }

  saveDSASettings() {
    if (!this.dsaSettingsView.validate()) {
      return Promise.resolve(false);
    }

    if (!this.modified) {
      return Promise.resolve(true);
    }

    const { context } = this.options;
    const campaignContext = context.get('Campaign');
    const campaignParams = context.get('CampaignParams');
    const params = _.extend({}, campaignParams, this.dsaSettingsView.getValues());

    return jsdata.Campaign
      .create(params, { adapter: 'bulk', cacheResponse: false })
      .then(() => {
        const newCampaignContext = _.extend({}, campaignContext, this.dsaSettingsView.getValues(true));

        context.set(_.extend({}, context, {
          Campaign: newCampaignContext,
          CampaignParams: params,
        }));
        this.dsaSettingsView.setValues({
          disabled: true,
        });
        this.saveTargetingSource(context.get('Campaign').Id);
        this.modified = false;
        return true;
      })
      .catch((response) => {
        const hasErrors = response && !_.isUndefined(response.errors);

        if (hasErrors) {
          const campaignErrors = _.filter(response.errors, (error) => {
            return error.Type === 'Campaign Error';
          });

          if (campaignErrors.length) {
            const errors = i18nErrors.getBulkApiErrors(campaignErrors);

            this.dsaSettingsView.handleApiErrors(errors);
            this.scrollToDSAError();
          }

          const { updateViews } = this.autoTargetAdGroupCreateView;

          // no ad group will be created if dsa settings is not set correctly
          // we need to mark all editors with "hasChange = true" so that when user clicks "save & next"
          // UI will submit all unsaved adgroups
          updateViews.forEach((view) => {
            view.hasChanges = true;
          });
        }
        return false;
      })
      .finally(withUiBlock());
  }

  initializeAutoTargetAdGroupCreateView() {
    const AutoTargetAdGroupCreateView = autoTargetAdGroupCreateDecorator.decorate(AdGroupView);
    const AutoTargetAdGroupUpdateView = autoTargetAdGroupUpdateDecorator.decorate(AdGroupUpdateView);

    this.autoTargetAdGroupCreateView = new AutoTargetAdGroupCreateView(_.extend({}, this.options, {
      UpdateView: AutoTargetAdGroupUpdateView,
      submitDSASettings: () => {
        if (this.viewModel.hasDSASettings()) {
          return this.saveDSASettings();
        }
        return Promise.resolve(true);
      },
      validateDSASettings: () => {
        if (this.viewModel.hasDSASettings()) {
          if (!this.dsaSettingsView.validate()) {
            this.scrollToDSAError();
            return false;
          }
          return true;
        }
        return true;
      },
      isOptional: true,
      checkAdGroupNameDuplicates: this.checkAllAdGroupNameDuplicate.bind(this),
      isAutoTargetCampaign: true,
    }));

    this.autoTargetAdGroupCreateView.on('createAdGroupEditorView', (view) => {
      const hasDSASettings = this.hasDSASettingsInCampaignSettings();

      const dsaSettings = hasDSASettings ? {
        DomainName: this.options.context.get('Campaign').DomainName,
        Language: this.options.context.get('Campaign').DomainLanguage,
        Source: this.options.context.get('Campaign').TargetingSource,
      } : this.dsaSettingsView.getValues();

      view.setAutoTargetsViewSetting({
        Language: dsaSettings.LanguageCode,
        DomainName: dsaSettings.DomainName,
        Source: dsaSettings.Source,
      });
    });
  }

  initializeKeywordAdGroupCreateView() {
    const KeywordAdGroupCreateView = keywordAdGroupCreateDecorator.decorate(AdGroupView);
    const KeywordAdGroupUpdateView = keywordAdGroupUpdateDecorator.decorate(AdGroupUpdateView);

    this.keywordAdGroupCreateView = new KeywordAdGroupCreateView(_.extend({}, this.options, {
      UpdateView: KeywordAdGroupUpdateView,
      isOptional: true,
      checkAdGroupNameDuplicates: this.checkAllAdGroupNameDuplicate.bind(this),
      isMixedModeCampaignCreation: true,
    }));
  }

  updateSubViewDSASettings() {
    const dsaSettings = this.dsaSettingsView.getValues();

    if (this.autoTargetAdGroupCreateView) {
      this.autoTargetAdGroupCreateView.trigger('updateAutoTargetEditorSetting', {
        Language: dsaSettings.LanguageCode,
        DomainName: dsaSettings.DomainName,
        Source: dsaSettings.Source,
      });
    }
  }

  initializeDSASettings() {
    this.dsaSettingsView = new DSASettingsView();

    this.dsaSettingsView.on('change', () => {
      this.updateSubViewDSASettings();
      this.modified = true;
    });
  }

  toggleDSASettings() {
    this.viewModel.hasDSASettings(!this.hasDSASettingsInCampaignSettings());
  }

  adjustSectionOrder() {
    if (!this.mounted) {
      const hasDSASettings = this.hasDSASettingsInCampaignSettings();
      const $sectionRoot = this.$('.mixed-mode-adgroup-create');
      const $dynamicAdGroupSection = this.$('#dynamicAdGroupSection');
      const $standardAdGroupSection = this.$('#standardAdGroupSection');
      const $standardAdGroupSectionContainer = this.$('.standard-adgroup-creation-section-container');
      const $dynamicAdGroupSectionContainer = this.$('.dynamic-adgroup-creation-section-container');

      if (hasDSASettings) {
        // put standard ad group section to the end
        $sectionRoot.append($standardAdGroupSectionContainer);
        $dynamicAdGroupSection.collapse('show');
        $standardAdGroupSection.collapse('hide');
        this.removeStandardAdGroupSectionAriaExpandedAttribute();
        this.viewModel.isDynamicAdGroupSectionExpanded(true);
        this.viewModel.isStandardAdGroupSectionExpanded(false);
      } else {
        // put dynamic ad group section to the end
        $sectionRoot.append($dynamicAdGroupSectionContainer);
        $standardAdGroupSection.collapse('show');
        this.removeStandardAdGroupSectionAriaExpandedAttribute();
        $dynamicAdGroupSection.collapse('hide');
        this.viewModel.isStandardAdGroupSectionExpanded(true);
        this.viewModel.isDynamicAdGroupSectionExpanded(false);
      }
    }

    const hasDisclaimerSettings = this.hasDisclaimerSettings();

    this.viewModel.hasDisclaimerSettings(hasDisclaimerSettings);
    this.viewModel.isDomesticSimplifiedChineseEnabled(pageContext.Permissions && pageContext.Permissions.IsDomesticSimplifiedChineseEnabled);
    this.viewModel.allowDisclaimer(true);
  }

  onToggleSection(evt) {
    const { target } = evt;
    const { id } = target;

    if (id === 'dynamicAdGroupSection') {
      this.viewModel.isDynamicAdGroupSectionExpanded(!this.viewModel.isDynamicAdGroupSectionExpanded());
    } else if (id === 'standardAdGroupSection') {
      this.viewModel.isStandardAdGroupSectionExpanded(!this.viewModel.isStandardAdGroupSectionExpanded());
    }

    this.removeStandardAdGroupSectionAriaExpandedAttribute();
  }

  render() {
    this.$el.empty().append(tmpl({
      i18n,
    }));
    ko.applyBindings(this.viewModel, this.el);

    this.$('#dynamicAdGroupSection .dynamic-ad-group-create-view-container').empty().append(this.autoTargetAdGroupCreateView.render().$el);
    this.$('#standardAdGroupSection .standard-ad-group-create-view-container').empty().append(this.keywordAdGroupCreateView.render().$el);
    this.$('#dynamicAdGroupSection .dsa-settings-container').empty().append(this.dsaSettingsView.render().$el);
    return this;
  }

  remove() {
    if (this.dsaSettingsView) {
      this.dsaSettingsView.remove();
    }

    if (this.autoTargetAdGroupCreateView) {
      this.autoTargetAdGroupCreateView.remove();
    }

    if (this.keywordAdGroupCreateView) {
      this.keywordAdGroupCreateView.remove();
    }

    super.remove();
  }

  updateContext() {
    const standardAdGroups = this.keywordAdGroupCreateView.ko_model.adGroups();
    const dynamicAdGroups = this.autoTargetAdGroupCreateView.ko_model.adGroups();

    const contextAdGroups = _.map(standardAdGroups.concat(dynamicAdGroups), (adGroup) => {
      return adGroup.getContextObject();
    });

    this.options.context.set({ 'AdGroups': contextAdGroups });
  }

  validateIfThereIsAtLeast1AdGroup() {
    this.viewModel.errors([]);
    const standardAdGroups = this.keywordAdGroupCreateView.ko_model.adGroups();
    const dynamicAdGroups = this.autoTargetAdGroupCreateView.ko_model.adGroups();

    if (standardAdGroups.length === 0 && dynamicAdGroups.length === 0) {
      this.viewModel.errors([i18n.getString('MixedMode_AdGroupCreation_Warning_At_Least_1_AdGroup')]);
      scrollUtil.scrollAndCenter(this.$('ul.alert.alert-danger'));
      return false;
    }
    return true;
  }

  removeStandardAdGroupSectionAriaExpandedAttribute() {
    // We are using Bootstrap v3 collapse to show/hide the sections, and it is adding the incorrect aria-expanded attribute to non interactive elements.
    // So we need to manually remove it.
    setTimeout(() => {
      this.$('#standardAdGroupSection').removeAttr('aria-expanded');
    });
  }

  shouldNext() {
    if (this.validateIfThereIsAtLeast1AdGroup()) {
      return Promise.all([
        this.keywordAdGroupCreateView.save(true),
        this.autoTargetAdGroupCreateView.save(true),
      ])
        .then(([keywordAdGroupCreationResult, dynamicAdGroupCreationResult]) => {
          const $dynamicAdGroupSection = this.$('#dynamicAdGroupSection');
          const $standardAdGroupSection = this.$('#standardAdGroupSection');

          if (!keywordAdGroupCreationResult) {
            $standardAdGroupSection.collapse('show');
            this.removeStandardAdGroupSectionAriaExpandedAttribute();
          }

          if (!dynamicAdGroupCreationResult) {
            $dynamicAdGroupSection.collapse('show');
          }

          return keywordAdGroupCreationResult && dynamicAdGroupCreationResult;
        })
        .then((result) => {
          if (result) {
            this.updateContext();
          }
          return result;
        });
    }

    return Promise.resolve(false);
  }

  shouldCancel() {
    if (this.validateIfThereIsAtLeast1AdGroup()) {
      return Promise.all([
        this.keywordAdGroupCreateView.shouldCancel(),
        this.autoTargetAdGroupCreateView.shouldCancel(),
      ]).then(([standardAdGroupCreateResult, dynamicAdGroupCreateResult]) => {
        return standardAdGroupCreateResult && dynamicAdGroupCreateResult;
      });
    }
    return Promise.resolve(false);
  }
}
